export type CommercialPageType = "experience_collection" | "guide" | "place" | "transport" | "route" | "other";
// Keep this aligned with api.commercial_interactions' validated vocabulary.
export type CommercialSurface = "experience_card" | "recommendation_card" | "mobile_rail" | "place_experience_rail" | "transport_commercial_card" | "guide_activity" | "guide_food";
export type CommercialRelationship = "none" | "affiliate_network" | "direct_referral";
export type CommercialBookingMode = "instant_booking" | "request_availability" | "contact_operator" | "official_booking";

export type CommercialItemIdentity = {
  id?: string | null;
  experience_id?: string | null;
  offer_id?: string | null;
  placement_id?: string | null;
  operator_id?: string | null;
  provider?: string | null;
  provider_key?: string | null;
  offer_provider_key?: string | null;
  external_product_key?: string | null;
  legacy_card_id?: string | null;
  booking_mode?: CommercialBookingMode | null;
  commercial_relationship?: CommercialRelationship | null;
  promotion_status?: "editorial" | "sponsored" | null;
  placement_target_type?: "guide" | "transport_page" | null;
  slot_key?: string | null;
  recommendation_intent?: string | null;
  url?: string | null;
  booking_url?: string | null;
  affiliate_url?: string | null;
};

export type CommercialContext = {
  pageType: CommercialPageType;
  surface: CommercialSurface;
  slot?: string | null;
  destination?: string | null;
  guideId?: string | null;
  placeId?: string | null;
  pagePath?: string | null;
};

export type CommercialInteraction = {
  offer_id: string | null;
  placement_id: string | null;
  legacy_card_id: string | null;
  provider_key: string | null;
  external_product_key: string | null;
  commercial_relationship: CommercialRelationship;
  booking_mode: CommercialBookingMode;
  promotion_status: "editorial" | "sponsored" | null;
  page_path: string;
  page_type: CommercialPageType;
  surface: CommercialSurface;
  slot_key: string | null;
  destination_key: string | null;
  guide_id: string | null;
  place_id: string | null;
  recommendation_intent: string | null;
};

function clean(value: unknown, max = 180) {
  return typeof value === "string" ? value.trim().slice(0, max) || null : null;
}

function currentPath(fallback?: string | null) {
  const path = clean(fallback, 500) || (typeof window !== "undefined" ? window.location.pathname : "/");
  return path && /^\/[A-Za-z0-9/_-]*$/.test(path) ? path : "/";
}

function stableLegacyIdentity(item: CommercialItemIdentity, context: CommercialContext) {
  const existing = clean(item.legacy_card_id || item.id, 100);
  if (existing) return existing;
  const rawUrl = clean(item.booking_url || item.affiliate_url || item.url, 2000) || "";
  let destination = rawUrl;
  try {
    const parsed = new URL(rawUrl);
    parsed.hash = "";
    destination = `${parsed.hostname.toLowerCase()}${parsed.pathname}?${[...parsed.searchParams.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => `${key}=${value}`).join("&")}`;
  } catch { /* a stable page/provider/slot key remains available */ }
  const seed = [currentPath(context.pagePath), context.surface, context.slot || item.slot_key || "", item.provider_key || item.provider || "", destination].join("|");
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) hash = Math.imul(hash ^ seed.charCodeAt(index), 16777619);
  return `legacy-${(hash >>> 0).toString(36)}`;
}

export function commercialInteraction(item: CommercialItemIdentity, context: CommercialContext): CommercialInteraction | null {
  const offerId = clean(item.offer_id, 80);
  // A legacy snapshot may be measured, but it must never be promoted to a
  // fabricated canonical Offer or Placement identity.
  const legacyCardId = offerId ? null : stableLegacyIdentity(item, context);
  if (!offerId && !legacyCardId) return null;
  return {
    offer_id: offerId,
    placement_id: offerId ? clean(item.placement_id, 80) : null,
    legacy_card_id: legacyCardId,
    provider_key: clean(item.offer_provider_key || item.provider_key || item.provider, 80),
    external_product_key: clean(item.external_product_key, 180),
    commercial_relationship: item.commercial_relationship || (offerId ? "none" : "affiliate_network"),
    booking_mode: item.booking_mode || "instant_booking",
    promotion_status: item.promotion_status || null,
    page_path: currentPath(context.pagePath),
    page_type: context.pageType,
    surface: context.surface,
    slot_key: clean(context.slot || item.slot_key, 80),
    destination_key: clean(context.destination, 80),
    guide_id: clean(context.guideId, 80),
    place_id: clean(context.placeId, 80),
    recommendation_intent: clean(item.recommendation_intent, 40),
  };
}

export function commercialImpressionKey(interaction: CommercialInteraction) {
  return [interaction.page_path, interaction.surface, interaction.slot_key || "", interaction.offer_id || `legacy:${interaction.legacy_card_id}`, interaction.placement_id || ""].join("|");
}
